import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchAbcBatchTargets,
  addAbcBatchTarget,
  updateAbcBatchTarget,
  deleteAbcBatchTarget,
  fetchFuelTypes,
  getAbcBatchStatus,
  runAbcBatch,
} from "../api/client";

export default function AdminAbcBatch() {
  const { t } = useTranslation();
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [fuelGroups, setFuelGroups] = useState([]);
  const [batchStatus, setBatchStatus] = useState(null);
  const [batchPollTimer, setBatchPollTimer] = useState(null);
  const [form, setForm] = useState({
    postal_code: "",
    litres: 1000,
    fuel_type: "burning_oil_summer",
    description: "",
    sort_order: "",
  });

  useEffect(() => {
    let ignore = false;
    async function loadInitial() {
      try {
        setLoading(true);
        setError("");
        const [list, fuels, status] = await Promise.all([
          fetchAbcBatchTargets(),
          fetchFuelTypes(),
          fetchStatus(),
        ]);
        if (!ignore) {
          setTargets(list || []);
          setFuelGroups(fuels || []);
          setBatchStatus(status || null);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.response?.data?.message || t("admin.abcBatch.errors.load"));
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }
    loadInitial();
    return () => {
      ignore = true;
      if (batchPollTimer) {
        clearTimeout(batchPollTimer);
      }
    };
  }, [batchPollTimer]);

  const fetchStatus = async () => {
    const status = await getAbcBatchStatus();
    setBatchStatus(status);
    return status;
  };

  const pollStatus = async () => {
    const status = await fetchStatus();
    if (status?.status === "running") {
      const timer = setTimeout(pollStatus, 3000);
      setBatchPollTimer(timer);
    }
  };

  const flattenedVariants = fuelGroups.flatMap((group) =>
    group.variants?.map((variant) => ({
      id: variant.id,
      label: `${group.label}: ${variant.label}`,
    })) || []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const refreshTargets = async () => {
    const list = await fetchAbcBatchTargets();
    setTargets(list || []);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setSuccessMsg("");
      await addAbcBatchTarget({
        postal_code: form.postal_code,
        litres: form.litres,
        fuel_type: form.fuel_type,
        description: form.description,
        sort_order:
          form.sort_order === "" ? undefined : Number(form.sort_order),
      });
      setForm({
        postal_code: "",
        litres: 1000,
        fuel_type: form.fuel_type,
        description: "",
        sort_order: "",
      });
      setSuccessMsg(t("admin.targets.added"));
      await refreshTargets();
    } catch (err) {
      setError(err.response?.data?.message || t("admin.abcBatch.errors.add"));
    }
  };

  const handleToggleActive = async (target) => {
    try {
      await updateAbcBatchTarget(target.id, { active: !target.active });
      await refreshTargets();
    } catch (err) {
      setError(err.response?.data?.message || t("admin.abcBatch.errors.update"));
    }
  };

  const handleDelete = async (target) => {
    if (!window.confirm(t("admin.abcBatch.confirmDelete", { postalCode: target.postal_code }))) return;
    try {
      await deleteAbcBatchTarget(target.id);
      await refreshTargets();
    } catch (err) {
      setError(err.response?.data?.message || t("admin.abcBatch.errors.delete"));
    }
  };

  const handleRunBatch = async () => {
    try {
      setError("");
      setSuccessMsg("");
      setBatchStatus({ status: "running" });
      await runAbcBatch(false);
      pollStatus();
    } catch (err) {
      setBatchStatus(null);
      setError(err.response?.data?.message || t("admin.abcBatch.errors.run"));
    }
  };

  const handleSortChange = async (target, value) => {
    try {
      await updateAbcBatchTarget(target.id, {
        sort_order: value === "" ? null : Number(value),
      });
      await refreshTargets();
    } catch (err) {
      setError(err.response?.data?.message || t("admin.abcBatch.errors.order"));
    }
  };

  return (
    <div className="bg-white border rounded-xl shadow p-4 space-y-4">
      <h2 className="text-xl font-semibold">{t("admin.abcBatch.title")}</h2>
      <p className="text-sm text-gray-600">
        {t("admin.abcBatch.description")}
      </p>
      {batchStatus && (
        <div className="p-3 bg-gray-50 border rounded text-sm flex flex-wrap gap-3 items-center">
          <div>
            <span className="font-semibold">{t("admin.abcBatch.status")}:</span>{" "}
            {batchStatus.status || "—"}
          </div>
          {batchStatus.started_at && (
            <div>
              <span className="font-semibold">{t("admin.abcBatch.started")}:</span>{" "}
              {new Date(batchStatus.started_at).toLocaleString("fi-FI")}
            </div>
          )}
          {batchStatus.finished_at && (
            <div>
              <span className="font-semibold">{t("admin.abcBatch.finished")}:</span>{" "}
              {new Date(batchStatus.finished_at).toLocaleString("fi-FI")}
            </div>
          )}
          {(batchStatus.success_count !== undefined ||
            batchStatus.total_targets !== undefined) && (
            <div>
              <span className="font-semibold">{t("admin.abcBatch.results")}:</span>{" "}
              {batchStatus.success_count ?? 0}/{batchStatus.total_targets ?? "?"}
            </div>
          )}
          {batchStatus.failure_reason && (
            <div className="text-red-600">
              <span className="font-semibold">{t("admin.abcBatch.error")}:</span>{" "}
              {batchStatus.failure_reason}
            </div>
          )}
          {batchStatus.status === "running" && (
            <div className="text-blue-600">{t("admin.abcBatch.fetching")}</div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchStatus}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              {t("admin.abcBatch.refreshStatus")}
            </button>
            <button
              type="button"
              onClick={pollStatus}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              {t("admin.abcBatch.follow")}
            </button>
            <button
              type="button"
              onClick={handleRunBatch}
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-500"
              disabled={batchStatus.status === "running"}
            >
              {t("admin.abcBatch.run")}
            </button>
          </div>
        </div>
      )}
      {(error || successMsg) && (
        <div
          className={`p-2 rounded text-sm ${
            error ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
          }`}
        >
          {error || successMsg}
        </div>
      )}
      <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.abcBatch.postal")}
          </label>
          <input
            name="postal_code"
            value={form.postal_code}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            placeholder={t("admin.abcBatch.postalPlaceholder")}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.abcBatch.litres")}
          </label>
          <input
            type="number"
            name="litres"
            value={form.litres}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            min="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.targets.fuelType")}
          </label>
          <select
            name="fuel_type"
            value={form.fuel_type}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 bg-white"
          >
            {flattenedVariants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.abcBatch.descriptionLabel")}
          </label>
          <input
            name="description"
            value={form.description}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            placeholder={t("admin.abcBatch.optionalInfo")}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.abcBatch.order")}
          </label>
          <input
            name="sort_order"
            value={form.sort_order}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            placeholder={t("admin.abcBatch.orderHint")}
          />
        </div>
        <div className="md:col-span-5 flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
          >
            {t("admin.abcBatch.add")}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">{t("admin.abcBatch.postal")}</th>
              <th className="p-2">{t("admin.abcBatch.litres")}</th>
              <th className="p-2">{t("admin.abcBatch.fuel")}</th>
              <th className="p-2">{t("admin.abcBatch.descriptionLabel")}</th>
              <th className="p-2">{t("admin.abcBatch.order")}</th>
              <th className="p-2">{t("admin.abcBatch.active")}</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {targets.map((target) => (
              <tr key={target.id} className="border-b last:border-none">
                <td className="p-2 font-semibold">{target.postal_code}</td>
                <td className="p-2">{target.litres}</td>
                <td className="p-2">{target.fuel_type}</td>
                <td className="p-2">{target.description || "—"}</td>
                <td className="p-2">
                  <input
                    type="number"
                    className="w-24 border rounded px-2 py-1"
                    value={target.sort_order ?? ""}
                    onChange={(e) => handleSortChange(target, e.target.value)}
                  />
                </td>
                <td className="p-2">
                  <button
                    type="button"
                    onClick={() => handleToggleActive(target)}
                    className={`px-3 py-1 rounded text-xs font-semibold ${
                      target.active
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {target.active ? t("admin.abcBatch.active") : t("admin.abcBatch.off")}
                  </button>
                </td>
                <td className="p-2 text-right">
                  <button
                    type="button"
                    onClick={() => handleDelete(target)}
                    className="text-red-600 hover:underline"
                  >
                    Poista
                  </button>
                </td>
              </tr>
            ))}
            {!targets.length && !loading && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  {t("admin.abcBatch.noTargets")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
